import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from "react-router-dom";

function StudioRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/enhancer${search}`} replace />;
}

// Preserves /wellness/:id links by forwarding to canonical /offerings/:id
function RedirectWellness() {
  const { id } = useParams();
  return <Navigate to={`/offerings/${id}`} replace />;
}
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ActiveContextProvider } from "@/context/ActiveContext";
import { CartProvider } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DiscoveryPopup from "@/components/DiscoveryPopup";
import Walkthrough from "@/components/Walkthrough";
import InstallPrompt from "@/components/InstallPrompt";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import StartHere from "@/pages/StartHere";
import GaugePage from "@/pages/GaugePage";
import GaugeApp from "@/pages/GaugeApp";
import GaugeJourney from "@/pages/GaugeJourney";
import DailyCoach from "@/pages/DailyCoach";
import GuideCoachDashboard from "@/pages/GuideCoachDashboard";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import Testimonials from "@/pages/Testimonials";
import PrivacyCenter from "@/pages/PrivacyCenter";
import CookieBanner from "@/components/CookieBanner";
import SiteSEO from "@/components/SiteSEO";
import SchedulePage from "@/pages/SchedulePage";
import EventWatch from "@/pages/EventWatch";
import EventStudio from "@/pages/EventStudio";
import WellnessServices from "@/pages/WellnessServices";
import Memberships from "@/pages/Memberships";
import Packages from "@/pages/Packages";
import Community from "@/pages/Community";
import Discover from "@/pages/Discover";
import Showcase from "@/pages/Showcase";
import FeedbackSurveyPage from "@/pages/FeedbackSurveyPage";
import Member360Page from "@/pages/crm/Member360Page";
import PublicFormPage from "@/pages/PublicFormPage";
import GroupDetail from "@/pages/GroupDetail";
import Members from "@/pages/Members";
import Messages from "@/pages/Messages";
import EventPage from "@/pages/EventPage";
import ManageBooking from "@/pages/ManageBooking";
import OnlineSession from "@/pages/OnlineSession";
import MemberProfile from "@/pages/MemberProfile";
import Enhancers from "@/pages/Enhancers";
import Partner from "@/pages/Partner";
import EnhancerStorefront from "@/pages/EnhancerStorefront";
import Marketplace from "@/pages/Marketplace";
import Checkout from "@/pages/Checkout";
import ConnectDemo from "@/pages/ConnectDemo";
import Courses from "@/pages/Courses";
import Media from "@/pages/Media";
import DynamicPage from "@/pages/DynamicPage";
import NotFound from "@/pages/NotFound";
import CourseDetail from "@/pages/CourseDetail";
import MyLearning from "@/pages/MyLearning";
import MyCalendar from "@/pages/MyCalendar";
import PublicCalendar from "@/pages/PublicCalendar";
import Certificate from "@/pages/Certificate";
import Ticket from "@/pages/Ticket";
import Stewardship from "@/pages/Stewardship";
import VendorApply from "@/pages/VendorApply";
import EnhancerApply from "@/pages/EnhancerApply";
import Locations from "@/pages/Locations";
import Online from "@/pages/Online";
import LocationStorefront from "@/pages/LocationStorefront";
import LocationManagerDashboard from "@/pages/LocationManagerDashboard";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import SuggestSite from "@/pages/SuggestSite";
import Agreements from "@/pages/Agreements";
import Policies from "@/pages/Policies";
import LegalPage from "@/pages/LegalPage";
import DataRequest from "@/pages/DataRequest";
import Join from "@/pages/Join";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import OrderHistory from "@/pages/OrderHistory";
import EnhancerDashboard from "@/pages/EnhancerDashboard";
import CouncilDashboard from "@/pages/CouncilDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import RedeemGift from "@/pages/RedeemGift";
import ConfirmIntake from "@/pages/ConfirmIntake";
import GiftCertificate from "@/pages/GiftCertificate";
import GiftCardBalance from "@/pages/GiftCardBalance";
import BookingAgreementsSign from "@/pages/BookingAgreementsSign";
import MyPrograms from "@/pages/MyPrograms";
import SetPassword from "@/pages/SetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import POS from "@/pages/pos/POS";
import HelpCenter from "@/pages/HelpCenter";
import HelpArticle from "@/pages/HelpArticle";
import ProfileSettings from "@/pages/ProfileSettings";

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F9F7F3]">
      <Navbar />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <DiscoveryPopup />
      <Walkthrough />
      <CartDrawer />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-[#4A5D6B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-[#4A5D6B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.role !== "staff") return <Navigate to="/dashboard" replace />;
  return children;
}

function POSRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-[#4A5D6B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!["admin", "staff", "enhancer", "location_manager"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ActiveContextProvider>
      <CartProvider>
        <Toaster position="top-right" richColors />
        <InstallPrompt />
        <BrowserRouter>
          <SiteSEO />
        <CookieBanner />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/start" element={<StartHere />} />
            <Route path="/gauge" element={<GaugePage />} />
            <Route path="/gauge/app" element={<GaugeApp />} />
            <Route path="/gauge/journey" element={<ProtectedRoute><GaugeJourney /></ProtectedRoute>} />
            <Route path="/coach" element={<ProtectedRoute><DailyCoach /></ProtectedRoute>} />
            <Route path="/guide-coach" element={<ProtectedRoute><GuideCoachDashboard /></ProtectedRoute>} />
            <Route path="/wellness" element={<Navigate to="/offerings" replace />} />
            <Route path="/wellness/:id" element={<RedirectWellness />} />
            <Route path="/offerings" element={<WellnessServices />} />
            <Route path="/offerings/:id" element={<WellnessServices />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community/groups/:slug" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
            <Route path="/community/members" element={<ProtectedRoute><Members /></ProtectedRoute>} />
            <Route path="/community/members/:id" element={<ProtectedRoute><MemberProfile /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/events" element={<SchedulePage />} />
            <Route path="/calendar" element={<PublicCalendar />} />
            <Route path="/events/:id" element={<SchedulePage />} />
            <Route path="/events/:id/watch" element={<EventWatch />} />
            <Route path="/events/:id/studio" element={<EventStudio />} />
            <Route path="/gatherings/:id" element={<EventPage />} />
            <Route path="/schedule" element={<Navigate to="/events" replace />} />
            <Route path="/memberships" element={<Memberships />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/gift-card-balance" element={<GiftCardBalance />} />
            <Route path="/booking-agreements/:token" element={<BookingAgreementsSign />} />
            <Route path="/programs" element={<ProtectedRoute><MyPrograms /></ProtectedRoute>} />
            <Route path="/enhancers" element={<Enhancers />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/feedback-survey" element={<FeedbackSurveyPage />} />
            <Route path="/f/:token" element={<PublicFormPage />} />
            <Route path="/crm/member/:userId" element={<ProtectedRoute><Member360Page /></ProtectedRoute>} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/enhancers/:id" element={<EnhancerStorefront />} />
            <Route path="/manage-booking/:id" element={<ManageBooking />} />
            <Route path="/session/:id" element={<OnlineSession />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/connect-demo" element={<ConnectDemo />} />
            <Route path="/marketplace/:id" element={<Marketplace />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/media" element={<Media />} />
            <Route path="/media/:id" element={<Media />} />
            <Route path="/ticket/:id" element={<Ticket />} />
            <Route path="/p/:slug" element={<DynamicPage />} />
            <Route path="*" element={<NotFound />} />
            <Route path="/courses/:id" element={<CourseDetail />} />
            <Route path="/courses/:id/certificate" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
            <Route path="/my-learning" element={<ProtectedRoute><MyLearning /></ProtectedRoute>} />
            <Route path="/my-calendar" element={<ProtectedRoute><MyCalendar /></ProtectedRoute>} />
            <Route path="/stewardship" element={<Stewardship />} />
            <Route path="/vendor/apply" element={<VendorApply />} />
            <Route path="/enhancer/apply" element={<ProtectedRoute><EnhancerApply /></ProtectedRoute>} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/online" element={<Online />} />
            <Route path="/locations/:slug" element={<LocationStorefront />} />
            <Route path="/centers/:slug" element={<LocationStorefront />} />
            <Route path="/manager" element={<ProtectedRoute><LocationManagerDashboard /></ProtectedRoute>} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:id" element={<ArticleDetail />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/suggest" element={<SuggestSite />} />
            <Route path="/agreements" element={<Agreements />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/privacy" element={<LegalPage type="privacy" fallbackTitle="Privacy Policy" />} />
            <Route path="/cancellation" element={<LegalPage type="cancellation" fallbackTitle="Cancellation Policy" />} />
            <Route path="/refunds" element={<LegalPage type="refund" fallbackTitle="Refund Policy" />} />
            <Route path="/terms" element={<LegalPage type="terms" fallbackTitle="Terms of Participation" />} />
            <Route path="/data-request" element={<DataRequest />} />
            <Route path="/privacy-center" element={<ProtectedRoute><PrivacyCenter /></ProtectedRoute>} />
            <Route path="/settings/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help/:slug" element={<HelpArticle />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
            <Route path="/enhancer" element={<ProtectedRoute><EnhancerDashboard /></ProtectedRoute>} />
            <Route path="/studio" element={<StudioRedirect />} />
            <Route path="/council" element={<ProtectedRoute><CouncilDashboard /></ProtectedRoute>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Route>
          <Route path="/join" element={<Join />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/redeem" element={<RedeemGift />} />
          <Route path="/confirm" element={<ConfirmIntake />} />
          <Route path="/gift-certificate" element={<GiftCertificate />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/pos" element={<POSRoute><POS /></POSRoute>} />
        </Routes>
      </BrowserRouter>
      </CartProvider>
      </ActiveContextProvider>
    </AuthProvider>
  );
}

export default App;
